import { useRef } from "react";
import {
  motion,
  useMotionValue,
  useScroll,
  useSpring,
  useTransform,
} from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { FadeUp, MaskLine } from "./motion";
import { scrollToSection } from "./Nav";

const PORTRAIT = "/assets/headshot.jpg";

const STATS = [
  { value: "60+", label: "Books published" },
  { value: "3.2M", label: "Words — record collection" },
  { value: "Top 1%", label: "ACBSP exam, globally" },
  { value: "4.0", label: "Doctoral GPA" },
];

function TiltPortrait({ scrollYProgress }) {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [7, -7]), {
    stiffness: 140,
    damping: 18,
  });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-9, 9]), {
    stiffness: 140,
    damping: 18,
  });
  const parallaxY = useTransform(scrollYProgress, [0, 1], [0, 90]);

  const onMove = (e) => {
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    mx.set((e.clientX - rect.left) / rect.width - 0.5);
    my.set((e.clientY - rect.top) / rect.height - 0.5);
  };
  const onLeave = () => {
    mx.set(0);
    my.set(0);
  };

  return (
    <motion.div
      style={{ y: parallaxY }}
      className="relative mx-auto w-full max-w-md lg:max-w-none"
    >
      <motion.div
        ref={ref}
        data-testid="hero-portrait-card"
        onMouseMove={onMove}
        onMouseLeave={onLeave}
        style={{ rotateX, rotateY, transformPerspective: 1000 }}
        initial={{ opacity: 0, scale: 0.94 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 1.2, delay: 0.9, ease: [0.16, 1, 0.3, 1] }}
        className="group relative"
      >
        <div className="relative overflow-hidden border border-line">
          <img
            src={PORTRAIT}
            alt="Dr. Nolan Ryan Forbes — portrait"
            className="aspect-[4/5] w-full object-cover object-[50%_15%] grayscale contrast-110 transition-transform duration-700 group-hover:scale-[1.03]"
          />
          <div className="pointer-events-none absolute inset-0 spotlight opacity-80" />
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-ink/70 via-transparent to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 flex items-center justify-between border-t border-line bg-ink/60 px-5 py-4 backdrop-blur-md">
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-ash">
              Nolan Ryan Forbes
            </span>
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-vermilion">
              Est. Texas
            </span>
          </div>
          <span className="absolute left-0 top-0 h-5 w-5 border-l-2 border-t-2 border-vermilion" />
          <span className="absolute right-0 top-0 h-5 w-5 border-r-2 border-t-2 border-vermilion" />
        </div>
      </motion.div>
      <div
        aria-hidden="true"
        className="absolute -right-4 -top-4 hidden h-full w-full border border-vermilion/25 lg:block"
      />
    </motion.div>
  );
}

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const textY = useTransform(scrollYProgress, [0, 1], [0, -70]);

  return (
    <section
      id="top"
      ref={ref}
      className="relative flex min-h-screen flex-col overflow-hidden pt-28 sm:pt-32"
    >
      <div
        aria-hidden="true"
        className="grid-lines pointer-events-none absolute inset-0"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -right-40 -top-40 h-[36rem] w-[36rem] rounded-full bg-vermilion/10 blur-[140px]"
      />

      <motion.div
        style={{ y: textY }}
        className="relative mx-auto grid w-full max-w-[1600px] flex-1 grid-cols-1 items-center gap-14 px-6 sm:px-12 lg:grid-cols-12 lg:gap-10 lg:px-24"
      >
        <div className="lg:col-span-7">
          <div className="overflow-hidden">
            <motion.p
              data-testid="hero-eyebrow"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
              className="mb-8 font-mono text-[11px] uppercase tracking-[0.3em] text-vermilion sm:text-xs"
            >
              Author — Technologist — Entrepreneur —
              <br className="sm:hidden" /> Psychological Researcher
            </motion.p>
          </div>

          <h1
            data-testid="hero-title"
            className="font-serif text-[clamp(4.2rem,12vw,11rem)] font-light uppercase leading-[0.85] tracking-tight"
          >
            <MaskLine delay={0.35}>Nolan</MaskLine>
            <MaskLine delay={0.47}>
              <span className="text-outline">Ryan</span>
            </MaskLine>
            <MaskLine delay={0.59}>
              Forbes<span className="text-vermilion">.</span>
            </MaskLine>
          </h1>

          <motion.p
            data-testid="hero-subtitle"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.05, ease: [0.16, 1, 0.3, 1] }}
            className="mt-8 max-w-lg text-lg leading-relaxed text-ash"
          >
            Founder of{" "}
            <span className="text-bone">Cultural Behavioral Psychology</span>.
            Author of 60+ books — including the record-breaking{" "}
            <span className="text-bone">3.2-million-word</span> A–Z Computer
            Programming Collection — working at the intersection of technology,
            psychology, and the arts.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.2, ease: [0.16, 1, 0.3, 1] }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-cta-button"
              onClick={() => scrollToSection("#books")}
              className="group inline-flex items-center gap-3 border border-vermilion bg-vermilion px-8 py-4 font-mono text-[12px] uppercase tracking-[0.3em] text-ink transition-all duration-300 hover:bg-transparent hover:text-vermilion"
            >
              Explore the work
              <ArrowUpRight
                className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1"
                strokeWidth={1.5}
              />
            </button>
            <button
              data-testid="hero-secondary-cta"
              onClick={() => scrollToSection("#about")}
              className="group inline-flex items-center gap-3 border border-line px-8 py-4 font-mono text-[12px] uppercase tracking-[0.3em] text-ash transition-all duration-300 hover:border-bone hover:text-bone"
            >
              The scholar
              <ArrowDown
                className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-1"
                strokeWidth={1.5}
              />
            </button>
          </motion.div>
        </div>

        <div className="lg:col-span-5">
          <TiltPortrait scrollYProgress={scrollYProgress} />
        </div>
      </motion.div>

      <FadeUp
        delay={1.3}
        className="relative mx-auto w-full max-w-[1600px] px-6 pb-10 sm:px-12 lg:px-24"
      >
        <div
          data-testid="hero-stats-strip"
          className="grid grid-cols-2 border-t border-line md:grid-cols-4"
        >
          {STATS.map((s, i) => (
            <div
              key={s.label}
              className={`border-line py-6 pr-6 ${
                i !== 0 ? "md:border-l md:pl-8" : ""
              } ${i % 2 !== 0 ? "border-l pl-6 md:pl-8" : ""}`}
            >
              <p className="font-serif text-3xl text-bone sm:text-4xl">
                {s.value}
              </p>
              <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-smoke">
                {s.label}
              </p>
            </div>
          ))}
        </div>
      </FadeUp>
    </section>
  );
}